import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { canCreateOrEditCRM, canDeleteActivity } from "../lib/crm-permissions";
import {
  ASSIGNEE_UNASSIGNED,
  getAssigneeLabel,
  getTaskAssigneeLabel,
  toAssignedUserId,
} from "../lib/tasks-assignees";
import {
  buildTaskDetailEditForm,
  dateInputToIso,
  getTaskPrimaryRelation,
  getTaskRelationDetailEndpoint,
  getTaskRelationLink,
  isTaskOverdue,
} from "../lib/task-detail";
import PageHeader from "../components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import StatusPill from "../components/status/StatusPill";
import AddActivityDialog from "../components/activity/AddActivityDialog";
import QuickNoteDialog from "../components/activity/QuickNoteDialog";
import Timeline from "../components/activity/Timeline";
import { formatDate, formatDateTime } from "../lib/format";
import { PRIORITY, TASK_STATUS } from "../lib/enums";
import { PRIORITY_LABELS, TASK_STATUS_LABELS } from "../lib/labels";
import { mergeActivities, normalizeActivityPageResponse } from "../lib/activity-pagination";

const EMPTY_EDIT_FORM = buildTaskDetailEditForm(null);
const DETAIL_ACTIVITY_PAGE_SIZE = 10;

export default function TaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, isSuperAdmin } = useAuth();
  const perm = { role, isSuperAdmin };
  const canWrite = canCreateOrEditCRM(perm);
  const canDeleteActivities = canDeleteActivity(perm);

  const [task, setTask] = useState(null);
  const [relationItem, setRelationItem] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [assigneesError, setAssigneesError] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activitiesHasMore, setActivitiesHasMore] = useState(false);
  const [activitiesCursor, setActivitiesCursor] = useState(null);
  const [activitiesLoadingMore, setActivitiesLoadingMore] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [quickNoteDialogOpen, setQuickNoteDialogOpen] = useState(false);

  const assigneesById = useMemo(() => {
    const map = {};
    for (const user of assignees || []) {
      if (user?.id) map[user.id] = user;
    }
    return map;
  }, [assignees]);

  const assigneeOptions = useMemo(() => {
    const items = (assignees || [])
      .filter((user) => user?.id)
      .map((user) => ({ id: user.id, label: getAssigneeLabel(user) }));
    items.sort((a, b) => a.label.localeCompare(b.label, "de"));
    return items;
  }, [assignees]);

  const loadAssignees = useCallback(async () => {
    setAssigneesLoading(true);
    setAssigneesError("");
    try {
      const response = await api.get("/api/settings/users");
      setAssignees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setAssignees([]);
      setAssigneesError(extractApiError(error, "Teammitglieder konnten nicht geladen werden."));
    } finally {
      setAssigneesLoading(false);
    }
  }, []);

  const loadActivities = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (append) setActivitiesLoadingMore(true);
      try {
        const params = { task_id: id, limit: DETAIL_ACTIVITY_PAGE_SIZE };
        if (cursor?.created_at && cursor?.id) {
          params.before_created_at = cursor.created_at;
          params.before_id = cursor.id;
        }
        const response = await api.get("/api/activities/page", { params });
        const page = normalizeActivityPageResponse(response.data);
        setActivities((prev) => (append ? mergeActivities(prev, page.items) : page.items));
        setActivitiesHasMore(page.hasMore);
        setActivitiesCursor(page.nextCursor);
      } catch (error) {
        toast.error(extractApiError(error, "Aktivitäten konnten nicht geladen werden."));
        if (!append) {
          setActivities([]);
          setActivitiesHasMore(false);
          setActivitiesCursor(null);
        }
      } finally {
        if (append) setActivitiesLoadingMore(false);
      }
    },
    [id]
  );

  const handleDeleteActivity = useCallback(async (activity) => {
    try {
      await api.delete(`/api/activities/${activity.id}`);
      setActivities((prev) => prev.filter((item) => item.id !== activity.id));
      toast.success("Aktivität gelöscht.");
    } catch (error) {
      const msg = extractApiError(error, "Aktivität konnte nicht gelöscht werden.");
      toast.error(msg);
      throw error;
    }
  }, []);

  const loadTask = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    setLoadError("");
    setRelationItem(null);
    setActivities([]);
    setActivitiesHasMore(false);
    setActivitiesCursor(null);
    try {
      const response = await api.get(`/api/tasks/${id}`);
      const taskData = response.data;
      setTask(taskData);

      const relation = getTaskPrimaryRelation(taskData);
      const relationEndpoint = getTaskRelationDetailEndpoint(relation);
      await Promise.all([
        loadAssignees(),
        loadActivities(),
        relationEndpoint
          ? api
              .get(relationEndpoint)
              .then((relationResponse) => setRelationItem(relationResponse.data || null))
              .catch(() => setRelationItem(null))
          : Promise.resolve(),
      ]);
    } catch (error) {
      setTask(null);
      setActivities([]);
      setActivitiesHasMore(false);
      setActivitiesCursor(null);
      if (error?.response?.status === 404) {
        setNotFound(true);
        setLoadError("Die Aufgabe wurde nicht gefunden.");
      } else {
        const message = extractApiError(error, "Aufgabe konnte nicht geladen werden.");
        setLoadError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, [id, loadActivities, loadAssignees]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  const handleStatusChange = async (newStatus) => {
    if (!task || !canWrite || newStatus === task.status) return;
    setStatusSaving(true);
    try {
      const response = await api.patch(`/api/tasks/${task.id}`, { status: newStatus });
      setTask(response.data);
      loadActivities();
      toast.success("Aufgabe aktualisiert.");
    } catch (error) {
      toast.error(extractApiError(error, "Aktualisierung fehlgeschlagen."));
    } finally {
      setStatusSaving(false);
    }
  };

  const openEdit = () => {
    if (!task) return;
    setEditForm(buildTaskDetailEditForm(task));
    setEditOpen(true);
  };

  const handleEdit = async (event) => {
    event.preventDefault();
    if (!task) return;
    if (!editForm.title.trim()) {
      toast.error("Bitte einen Titel angeben.");
      return;
    }
    setEditSaving(true);
    try {
      const payload = {};
      if ((task.title || "") !== editForm.title) payload.title = editForm.title;
      if ((task.description || "") !== editForm.description) payload.description = editForm.description;
      if ((task.priority || PRIORITY.MEDIUM) !== editForm.priority) payload.priority = editForm.priority;
      if ((task.status || TASK_STATUS.OPEN) !== editForm.status) payload.status = editForm.status;

      const dueIso = dateInputToIso(editForm.due_date);
      const prevDueIso = task.due_date ? new Date(task.due_date).toISOString() : null;
      if (prevDueIso !== dueIso) payload.due_date = dueIso;

      const nextAssignedUserId = toAssignedUserId(editForm.assigned_user_id);
      const prevAssignedUserId = task.assigned_user_id || null;
      if (prevAssignedUserId !== nextAssignedUserId) payload.assigned_user_id = nextAssignedUserId;

      if (Object.keys(payload).length === 0) {
        setEditOpen(false);
        return;
      }

      const response = await api.patch(`/api/tasks/${task.id}`, payload);
      setTask(response.data);
      setEditOpen(false);
      loadActivities();
      toast.success("Aufgabe aktualisiert.");
    } catch (error) {
      toast.error(extractApiError(error, "Aufgabe konnte nicht aktualisiert werden."));
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-12 text-center">Wird geladen …</div>;
  }

  if (notFound) {
    return (
      <div>
        <BackButton onClick={() => navigate("/aufgaben")} />
        <Card>
          <CardHeader>
            <CardTitle>Aufgabe nicht gefunden</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground">{loadError || "Die Aufgabe wurde nicht gefunden."}</div>
            <Button variant="outline" onClick={() => navigate("/aufgaben")}>
              Zu Aufgaben
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!task) {
    return (
      <div>
        <BackButton onClick={() => navigate("/aufgaben")} />
        <Card>
          <CardHeader>
            <CardTitle>Aufgabe konnte nicht geladen werden</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground">{loadError || "Bitte erneut versuchen."}</div>
            <Button variant="outline" onClick={loadTask}>
              Erneut laden
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusMeta = TASK_STATUS_LABELS[task.status] || { de: task.status, tone: "muted" };
  const priorityMeta = PRIORITY_LABELS[task.priority] || { de: task.priority, tone: "muted" };
  const overdue = isTaskOverdue(task.due_date, task.status);
  const assignedUserLabel = task.assigned_user_id
    ? getTaskAssigneeLabel({ assignedUserId: task.assigned_user_id, usersById: assigneesById }) || "Unbekannt"
    : "Nicht zugewiesen";
  const relation = getTaskPrimaryRelation(task);
  const relationLink = getTaskRelationLink(relation, relationItem);

  return (
    <div>
      <BackButton onClick={() => navigate("/aufgaben")} />

      <PageHeader
        title={task.title}
        description={
          <span className="inline-flex items-center gap-2">
            <StatusPill tone={priorityMeta.tone}>{priorityMeta.de}</StatusPill>
            <StatusPill tone={statusMeta.tone}>{statusMeta.de}</StatusPill>
            <span className="text-xs text-muted-foreground">Erstellt am {formatDate(task.created_at)}</span>
          </span>
        }
        actions={
          canWrite ? (
            <Button type="button" variant="outline" onClick={openEdit}>
              <Pencil className="w-4 h-4 mr-1.5" /> Aufgabe bearbeiten
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Aufgabendetails</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Titel">
                <ReadOnlyValue>{task.title || "Nicht gesetzt"}</ReadOnlyValue>
              </Field>
              <Field label="Priorität">
                <div className="flex items-center gap-2">
                  <StatusPill tone={priorityMeta.tone}>{priorityMeta.de}</StatusPill>
                </div>
              </Field>
              <Field label="Status">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill tone={statusMeta.tone}>{statusMeta.de}</StatusPill>
                    {overdue ? <StatusPill tone="destructive">Überfällig</StatusPill> : null}
                  </div>
                  <Select
                    value={task.status}
                    onValueChange={handleStatusChange}
                    disabled={!canWrite || statusSaving}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(TASK_STATUS).map((status) => (
                        <SelectItem key={status} value={status}>
                          {TASK_STATUS_LABELS[status].de}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </Field>
              <Field label="Zugewiesen an">
                <ReadOnlyValue>{assignedUserLabel}</ReadOnlyValue>
              </Field>
              <Field label="Fälligkeitsdatum">
                <ReadOnlyValue>{task.due_date ? formatDate(task.due_date) : "Nicht gesetzt"}</ReadOnlyValue>
              </Field>
              <Field label="Erstellt am">
                <ReadOnlyValue>{formatDateTime(task.created_at)}</ReadOnlyValue>
              </Field>
              <Field label="Aktualisiert am">
                <ReadOnlyValue>{formatDateTime(task.updated_at)}</ReadOnlyValue>
              </Field>
              <div className="md:col-span-2">
                <Field label="Beschreibung">
                  <ReadOnlyText>{task.description || "Nicht gesetzt"}</ReadOnlyText>
                </Field>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-5 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Verknüpft mit</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {relationLink ? (
                <>
                  <div className="text-sm text-muted-foreground">{getRelationHeading(relation.type)}</div>
                  <button
                    type="button"
                    className="text-sm font-medium hover:underline text-left"
                    onClick={() => navigate(relationLink.to)}
                  >
                    {relationLink.label}
                  </button>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">Keine Verknüpfung vorhanden.</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Aktivitäten</CardTitle>
              {canWrite ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setQuickNoteDialogOpen(true)}>
                    <Pencil className="w-4 h-4 mr-2" /> Notiz
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActivityDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Aktivität
                  </Button>
                </div>
              ) : null}
            </CardHeader>
            <CardContent>
              <Timeline
                items={activities}
                usersById={assigneesById}
                emptyText="Noch keine Aktivitäten zu dieser Aufgabe."
                currentContext={{ type: "task", id: task.id }}
                canDelete={canDeleteActivities}
                onDelete={handleDeleteActivity}
              />
              <div className="mt-4 flex flex-col items-center gap-2">
                {activitiesHasMore ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => loadActivities({ cursor: activitiesCursor, append: true })}
                    disabled={activitiesLoadingMore}
                  >
                    {activitiesLoadingMore ? "Weitere Aktivitäten werden geladen …" : "Mehr Aktivitäten laden"}
                  </Button>
                ) : activities.length > 0 ? (
                  <div className="text-xs text-muted-foreground">Alle Aktivitäten geladen</div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <QuickNoteDialog
        open={quickNoteDialogOpen}
        onOpenChange={setQuickNoteDialogOpen}
        relationPayload={task ? { related_task_id: task.id } : null}
        onCreated={() => loadActivities()}
      />
      <AddActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        relationPayload={task ? { related_task_id: task.id } : null}
        onCreated={() => loadActivities()}
      />

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          if (editSaving) return;
          setEditOpen(open);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Aufgabe bearbeiten</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3">
            <Field label="Titel *">
              <Input
                value={editForm.title}
                onChange={(event) => setEditForm({ ...editForm, title: event.target.value })}
                autoFocus
              />
            </Field>
            <Field label="Beschreibung">
              <Textarea
                rows={3}
                value={editForm.description}
                onChange={(event) => setEditForm({ ...editForm, description: event.target.value })}
              />
            </Field>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Fälligkeitsdatum">
                <Input
                  type="date"
                  value={editForm.due_date}
                  onChange={(event) => setEditForm({ ...editForm, due_date: event.target.value })}
                />
              </Field>
              <Field label="Zugewiesen an">
                <Select
                  value={editForm.assigned_user_id}
                  onValueChange={(value) => setEditForm({ ...editForm, assigned_user_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ASSIGNEE_UNASSIGNED}>Nicht zugewiesen</SelectItem>
                    {assigneesLoading ? (
                      <SelectItem value="__loading__" disabled>
                        Wird geladen …
                      </SelectItem>
                    ) : null}
                    {!assigneesLoading &&
                    editForm.assigned_user_id &&
                    editForm.assigned_user_id !== ASSIGNEE_UNASSIGNED &&
                    !assigneesById[editForm.assigned_user_id] ? (
                      <SelectItem value={editForm.assigned_user_id}>Unbekannt</SelectItem>
                    ) : null}
                    {assigneeOptions.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assigneesError ? <div className="text-xs text-muted-foreground mt-1">{assigneesError}</div> : null}
              </Field>
              <Field label="Priorität">
                <Select
                  value={editForm.priority}
                  onValueChange={(value) => setEditForm({ ...editForm, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(PRIORITY).map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {PRIORITY_LABELS[priority].de}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(TASK_STATUS).map((status) => (
                      <SelectItem key={status} value={status}>
                        {TASK_STATUS_LABELS[status].de}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} disabled={editSaving}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={editSaving}>
                {editSaving ? "Speichern …" : "Speichern"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BackButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center mb-3"
    >
      <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zu Aufgaben
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ReadOnlyValue({ children }) {
  return <div className="text-sm text-foreground">{children}</div>;
}

function ReadOnlyText({ children }) {
  return (
    <div className="min-h-[96px] rounded-md border border-border bg-muted/20 px-3 py-2 text-sm whitespace-pre-wrap">
      {children}
    </div>
  );
}

function getRelationHeading(type) {
  if (type === "lead") return "Lead";
  if (type === "company") return "Firma";
  if (type === "contact") return "Kontakt";
  if (type === "deal") return "Deal";
  return "Verknüpfung";
}
